import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, addDoc, getDocs, deleteDoc, updateDoc, query, where } from 'firebase/firestore';
import { FaChurch, FaSave, FaClock, FaTrash, FaPlus, FaEdit, FaWhatsapp, FaQrcode, FaPlug, FaTimesCircle, FaCheckCircle, FaPaperPlane, FaSpinner } from 'react-icons/fa';

export default function ChurchSettings() {
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [noticeBoard, setNoticeBoard] = useState('');
  const [loading, setLoading] = useState(true);

  // === ESTADOS DOS CULTOS ===
  const [cults, setCults] = useState([]);
  const [cultName, setCultName] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('0');
  const [time, setTime] = useState('');
  const [isCultFormOpen, setIsCultFormOpen] = useState(false);
  const [editCultId, setEditCultId] = useState(null);

  // === ESTADOS DO WHATSAPP ===
  const [wpApiUrl, setWpApiUrl] = useState('');
  const [wpApiKey, setWpApiKey] = useState('');
  const [wpInstanceName, setWpInstanceName] = useState('');
  const [wpConnected, setWpConnected] = useState(false);
  const [wpEnabled, setWpEnabled] = useState(false);
  const [wpSendDay, setWpSendDay] = useState('4'); // Quinta
  const [wpSendHour, setWpSendHour] = useState('14'); // 14h
  const [wpMessageTemplate, setWpMessageTemplate] = useState(
    `Olá, *{musico}*! 🎵\n\nSegue sua escala para esta semana:\n\n{escalas}\n\n🙏 Deus abençoe!`
  );
  const [wpQrCode, setWpQrCode] = useState(null);
  const [wpStatus, setWpStatus] = useState(''); // 'checking', 'disconnected', 'connected', 'qr_loading'
  const [wpFeedback, setWpFeedback] = useState(null);
  const [wpSaving, setWpSaving] = useState(false);
  const [wpTestSending, setWpTestSending] = useState(false);
  const pollingRef = useRef(null);

  // === ESTADOS DO DISPARO MANUAL ===
  const [referenceWeek, setReferenceWeek] = useState('next');
  const [musiciansList, setMusiciansList] = useState([]);
  const [loadingMusicians, setLoadingMusicians] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendProgress, setSendProgress] = useState('');
  const [errorModal, setErrorModal] = useState(null); // { title, message }

  const settingsDocRef = doc(db, "church_settings", "config");
  const wpSettingsDocRef = doc(db, "church_settings", "whatsapp");
  const cultsRef = collection(db, "service_templates");

  const daysMap = { 0: 'Domingo', 1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta', 5: 'Sexta', 6: 'Sábado' };
  const hoursOptions = Array.from({ length: 24 }, (_, i) => ({ val: String(i), label: `${String(i).padStart(2, '0')}:00` }));

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      try {
        // Carrega configurações gerais (públicas)
        const docSnap = await getDoc(settingsDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setName(data.name || '');
          setLogoUrl(data.logoUrl || '');
          setNoticeBoard(data.noticeBoard || '');
        }

        // Carrega configurações do WhatsApp (privadas)
        const wpDocSnap = await getDoc(wpSettingsDocRef);
        if (wpDocSnap.exists()) {
          const data = wpDocSnap.data();
          setWpApiUrl(data.whatsappApiUrl || '');
          setWpApiKey(data.whatsappApiKey || '');
          setWpInstanceName(data.whatsappInstanceName || '');
          setWpConnected(data.whatsappConnected || false);
          setWpEnabled(data.whatsappEnabled || false);
          setWpSendDay(String(data.whatsappSendDay ?? '4'));
          setWpSendHour(String(data.whatsappSendHour ?? '14'));
          setWpMessageTemplate(data.whatsappMessageTemplate || `Olá, *{musico}*! 🎵\n\nSegue sua escala para esta semana:\n\n{escalas}\n\n🙏 Deus abençoe!`);
          if (data.whatsappConnected) {
            setWpStatus('connected');
          } else {
            setWpStatus('disconnected');
          }
        } else {
          setWpStatus('disconnected');
        }

        await loadCults();
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      }
      setLoading(false);
    };
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verificação periódica do status da conexão WhatsApp (a cada 30s)
  useEffect(() => {
    if (loading || !wpApiUrl || !wpApiKey || !wpInstanceName) return;
    
    let isMounted = true;
    
    const checkStatus = async () => {
      try {
        const status = await checkConnectionStatus();
        if (!isMounted) return;
        if (status === 'connected') {
          setWpConnected(true);
          setWpStatus('connected');
        } else if (status === 'disconnected') {
          setWpConnected(false);
          setWpStatus('disconnected');
        }
        // Se retornar 'error' ou 'no_config', não altera o estado atual
      } catch (e) {
        console.warn("Erro na verificação periódica do WhatsApp:", e);
      }
    };
    
    // Verificar imediatamente ao carregar
    checkStatus();
    
    const interval = setInterval(checkStatus, 30000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, wpApiUrl, wpApiKey, wpInstanceName]);

  // Recarregar lista de músicos do disparo manual quando as dependências mudarem
  useEffect(() => {
    if (!loading) {
      loadMusiciansForWeek(referenceWeek);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, referenceWeek]);

  const loadCults = async () => {
    const data = await getDocs(cultsRef);
    const list = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    list.sort((a, b) => {
      if (a.dayOfWeek !== b.dayOfWeek) {
        return a.dayOfWeek - b.dayOfWeek;
      }
      const timeA = a.time || '00:00';
      const timeB = b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
    setCults(list);
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await setDoc(settingsDocRef, { name, logoUrl, noticeBoard, updatedAt: new Date() }, { merge: true });
      alert("Configurações salvas!");
    } catch (error) { alert("Erro ao salvar."); }
  };

  const handleSaveCult = async (e) => {
    e.preventDefault();
    if (!cultName || !time) return;
    try {
      const cultData = { name: cultName, dayOfWeek: parseInt(dayOfWeek), time, updatedAt: new Date() };
      if (editCultId) {
        await updateDoc(doc(db, "service_templates", editCultId), cultData);
      } else {
        await addDoc(cultsRef, { ...cultData, createdAt: new Date() });
      }
      handleCancelCult();
      loadCults();
    } catch (error) { console.error(error); }
  };

  const handleEditCult = (cult) => {
    setCultName(cult.name); setDayOfWeek(String(cult.dayOfWeek)); setTime(cult.time);
    setEditCultId(cult.id); setIsCultFormOpen(true);
  };

  const handleCancelCult = () => {
    setCultName(''); setTime(''); setDayOfWeek('0'); setEditCultId(null); setIsCultFormOpen(false);
  };

  const handleDeleteCult = async (id) => {
    if (window.confirm("Excluir este culto?")) { await deleteDoc(doc(db, "service_templates", id)); loadCults(); }
  };

  // ============================================================
  // WHATSAPP: Salvar configurações da Evolution API
  // ============================================================
  const handleSaveWhatsApp = async () => {
    if (!wpApiUrl || !wpApiKey || !wpInstanceName) {
      setWpFeedback({ msg: 'Preencha todos os campos da API.', type: 'error' });
      setTimeout(() => setWpFeedback(null), 3000);
      return;
    }
    setWpSaving(true);
    try {
      await setDoc(wpSettingsDocRef, {
        whatsappApiUrl: wpApiUrl.replace(/\/+$/, ''), // Remove trailing slash
        whatsappApiKey: wpApiKey,
        whatsappInstanceName: wpInstanceName,
        whatsappEnabled: wpEnabled,
        whatsappSendDay: parseInt(wpSendDay),
        whatsappSendHour: parseInt(wpSendHour),
        whatsappMessageTemplate: wpMessageTemplate,
        updatedAt: new Date()
      }, { merge: true });
      setWpFeedback({ msg: 'Configurações do WhatsApp salvas!', type: 'success' });
    } catch (err) {
      console.error(err);
      setWpFeedback({ msg: 'Erro ao salvar.', type: 'error' });
    }
    setWpSaving(false);
    setTimeout(() => setWpFeedback(null), 3000);
  };

  // ============================================================
  // WHATSAPP: Verificar status da conexão
  // ============================================================
  const checkConnectionStatus = useCallback(async () => {
    if (!wpApiUrl || !wpApiKey || !wpInstanceName) return 'no_config';
    try {
      const resp = await fetch(`${wpApiUrl.replace(/\/+$/, '')}/instance/connectionState/${wpInstanceName}`, {
        headers: { apikey: wpApiKey }
      });
      if (!resp.ok) return 'error';
      const data = await resp.json();
      const state = data?.instance?.state || data?.state;
      return state === 'open' ? 'connected' : 'disconnected';
    } catch (err) {
      console.error("Erro ao verificar status:", err);
      return 'error';
    }
  }, [wpApiUrl, wpApiKey, wpInstanceName]);

  // ============================================================
  // WHATSAPP: Criar instância na Evolution API (se não existir)
  // ============================================================
  const ensureInstance = async () => {
    const url = wpApiUrl.replace(/\/+$/, '');
    try {
      // Tenta buscar a instância
      const resp = await fetch(`${url}/instance/connectionState/${wpInstanceName}`, {
        headers: { apikey: wpApiKey }
      });
      
      // Se retornou 404 (Not Found), significa que a instância não existe e precisamos criar
      if (resp.status === 404) {
        console.log(`Instância ${wpInstanceName} não encontrada. Criando...`);
        const createResp = await fetch(`${url}/instance/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: wpApiKey },
          body: JSON.stringify({
            instanceName: wpInstanceName,
            qrcode: true,
            integration: "WHATSAPP-BAILEYS"
          })
        });
        
        if (!createResp.ok) {
          const errText = await createResp.text();
          console.error("Erro ao criar instância:", errText);
        } else {
          const createData = await createResp.json();
          console.log(`Instância ${wpInstanceName} criada com sucesso!`, createData);
          // Se a criação já retornou QR code, retornar
          return createData;
        }
      }
    } catch (e) {
      console.error("Erro ao buscar/criar instância:", e);
    }
    return null;
  };

  // ============================================================
  // WHATSAPP: Conectar e gerar QR Code
  // ============================================================
  const handleConnectWhatsApp = async () => {
    if (!wpApiUrl || !wpApiKey || !wpInstanceName) {
      setWpFeedback({ msg: 'Salve as configurações da API antes de conectar.', type: 'error' });
      setTimeout(() => setWpFeedback(null), 3000);
      return;
    }

    setWpStatus('qr_loading');
    setWpQrCode(null);

    const url = wpApiUrl.replace(/\/+$/, '');

    try {
      // Garantir que a instância exista (pode retornar dados com QR se foi criada agora)
      const createData = await ensureInstance();
      
      // Tentar extrair QR code da resposta de criação
      let base64 = null;
      if (createData) {
        base64 = createData?.qrcode?.base64 || createData?.base64 || createData?.qrcode;
        if (typeof base64 === 'object') base64 = base64?.base64 || null;
      }
      
      // Se não obteve QR da criação, buscar via endpoint de connect
      if (!base64) {
        const resp = await fetch(`${url}/instance/connect/${wpInstanceName}`, {
          headers: { apikey: wpApiKey }
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          console.error("Erro ao conectar instância:", errData);
          setErrorModal({
            title: `Erro ao gerar QR Code (HTTP ${resp.status})`,
            message: JSON.stringify(errData, null, 2)
          });
          setWpStatus('disconnected');
          return;
        }

        const data = await resp.json();
        console.log("Resposta /instance/connect:", data);
        base64 = data?.qrcode?.base64 || data?.base64 || data?.qrcode;
        if (typeof base64 === 'object') base64 = base64?.base64 || null;
      }

      if (base64) {
        // Garantir que tem o prefixo data:image
        if (typeof base64 === 'string' && !base64.startsWith('data:')) {
          base64 = `data:image/png;base64,${base64}`;
        }
        setWpQrCode(base64);
        setWpStatus('waiting_scan');

        // Polling a cada 3 segundos para verificar a conexão
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = setInterval(async () => {
          const status = await checkConnectionStatus();
          if (status === 'connected') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setWpQrCode(null);
            setWpConnected(true);
            setWpStatus('connected');
            // Salvar status no Firestore
            await setDoc(wpSettingsDocRef, { whatsappConnected: true, updatedAt: new Date() }, { merge: true });
            setWpFeedback({ msg: 'WhatsApp conectado com sucesso!', type: 'success' });
            setTimeout(() => setWpFeedback(null), 4000);
          }
        }, 3000);

        // Timeout de 60 segundos para o QR Code
        setTimeout(() => {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          if (!wpConnected) {
            setWpQrCode(null);
            setWpStatus('disconnected');
            setWpFeedback({ msg: 'QR Code expirou. Tente novamente.', type: 'error' });
            setTimeout(() => setWpFeedback(null), 4000);
          }
        }, 60000);

      } else {
        // Se não retornou QR Code, pode já estar conectado
        const status = await checkConnectionStatus();
        if (status === 'connected') {
          setWpConnected(true);
          setWpStatus('connected');
          await setDoc(wpSettingsDocRef, { whatsappConnected: true, updatedAt: new Date() }, { merge: true });
          setWpFeedback({ msg: 'WhatsApp já estava conectado!', type: 'success' });
          setTimeout(() => setWpFeedback(null), 3000);
        } else {
          setWpStatus('disconnected');
          setErrorModal({
            title: 'QR Code não retornado',
            message: 'A Evolution API não retornou o QR Code. Verifique se a URL da API, a API Key e o nome da instância estão corretos.'
          });
        }
      }
    } catch (err) {
      console.error("Erro na conexão:", err);
      setWpStatus('disconnected');
      setErrorModal({
        title: 'Erro ao Conectar',
        message: err.message
      });
    }
  };

  // ============================================================
  // WHATSAPP: Desconectar
  // ============================================================
  const handleDisconnectWhatsApp = async () => {
    if (!window.confirm("Deseja desconectar o WhatsApp?")) return;

    const url = wpApiUrl.replace(/\/+$/, '');
    try {
      await fetch(`${url}/instance/logout/${wpInstanceName}`, {
        method: 'DELETE',
        headers: { apikey: wpApiKey }
      });
    } catch (err) {
      console.warn("Logout request:", err);
    }

    setWpConnected(false);
    setWpStatus('disconnected');
    setWpQrCode(null);
    if (pollingRef.current) clearInterval(pollingRef.current);

    await setDoc(wpSettingsDocRef, { whatsappConnected: false, updatedAt: new Date() }, { merge: true });
    setWpFeedback({ msg: 'WhatsApp desconectado.', type: 'success' });
    setTimeout(() => setWpFeedback(null), 3000);
  };

  // ============================================================
  // WHATSAPP: Teste de envio manual (mensagem genérica rápida)
  // ============================================================
  const handleTestSend = async () => {
    const testPhone = prompt("Digite o número de WhatsApp para teste (com DDD, ex: 11999999999):");
    if (!testPhone) return;

    setWpTestSending(true);
    const url = wpApiUrl.replace(/\/+$/, '');
    const digits = testPhone.replace(/\D/g, '');
    const fullPhone = digits.startsWith('55') ? digits : `55${digits}`;

    try {
      const resp = await fetch(`${url}/message/sendText/${wpInstanceName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: wpApiKey },
        body: JSON.stringify({
          number: fullPhone,
          text: "🎵 *Teste - Escala de Louvor*\n\nEsta é uma mensagem de teste do sistema de envio automático de escalas.\n\n✅ Se você recebeu esta mensagem, o sistema está funcionando corretamente!"
        })
      });

      if (resp.ok) {
        setWpFeedback({ msg: `Mensagem de teste enviada para ${fullPhone}!`, type: 'success' });
      } else {
        const errData = await resp.json().catch(() => ({}));
        console.error("Erro Evolution API (Teste):", errData);
        let errDetail = JSON.stringify(errData, null, 2);
        setErrorModal({
          title: `Erro ao enviar teste (HTTP ${resp.status})`,
          message: errDetail
        });
      }
    } catch (err) {
      setErrorModal({
        title: 'Erro de Rede',
        message: err.message
      });
    }
    setWpTestSending(false);
    setTimeout(() => setWpFeedback(null), 4000);
  };

  // ============================================================
  // DISPARO MANUAL: Métodos Auxiliares e Envio em Lote
  // ============================================================
  const getWeekRangeForType = (type) => {
    const baseDate = new Date();
    if (type === 'next') {
      baseDate.setDate(baseDate.getDate() + 7);
    }
    const start = new Date(baseDate);
    const day = start.getDay();
    start.setDate(start.getDate() - day);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { startStr: fmt(start), endStr: fmt(end) };
  };

  const formatPhoneToInternational = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 11) return null;
    if (digits.startsWith("55") && digits.length >= 12) return digits;
    return `55${digits}`;
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "";
    const [, month, day] = dateStr.split("-");
    return `${day}/${month}`;
  };

  const getDayOfWeekName = (dateStr) => {
    const daysMap = [
      "Domingo", "Segunda", "Terça", "Quarta",
      "Quinta", "Sexta", "Sábado",
    ];
    const [y, m, d] = dateStr.split("-");
    const date = new Date(y, m - 1, d);
    return daysMap[date.getDay()];
  };

  const loadMusiciansForWeek = async (type) => {
    setLoadingMusicians(true);
    try {
      const { startStr, endStr } = getWeekRangeForType(type);
      
      const q = query(collection(db, "schedules"), where("date", ">=", startStr), where("date", "<=", endStr));
      const schedulesSnap = await getDocs(q);
      
      if (schedulesSnap.empty) {
        setMusiciansList([]);
        setLoadingMusicians(false);
        return;
      }
      
      const musiciansMap = {};
      schedulesSnap.forEach((doc) => {
        const sched = doc.data();
        if (!sched.team || sched.team.length === 0) return;
        
        sched.team.forEach((member) => {
          if (!musiciansMap[member.id]) {
            musiciansMap[member.id] = {
              id: member.id,
              name: member.name,
              schedules: [],
              phone: '',
              checked: true,
              status: 'pending',
              errorMsg: ''
            };
          }
          musiciansMap[member.id].schedules.push({
            title: sched.title,
            date: sched.date,
            time: sched.time,
            role: member.role,
            repertoire: sched.repertoire || []
          });
        });
      });
      
      const musiciansSnap = await getDocs(collection(db, "musicians"));
      musiciansSnap.forEach((doc) => {
        const mData = doc.data();
        if (musiciansMap[doc.id]) {
          musiciansMap[doc.id].phone = mData.phone || '';
        }
      });
      
      const list = Object.values(musiciansMap);
      list.sort((a, b) => a.name.localeCompare(b.name));
      
      setMusiciansList(list);
    } catch (err) {
      console.error("Erro ao carregar músicos da semana:", err);
    }
    setLoadingMusicians(false);
  };

  const handleSendReminders = async () => {
    const selected = musiciansList.filter(m => m.checked);
    if (selected.length === 0) {
      alert("Nenhum músico selecionado.");
      return;
    }
    if (!wpConnected) {
      alert("WhatsApp não está conectado. Conecte-o antes de disparar.");
      return;
    }
    
    setSendingReminders(true);
    let sentCount = 0;
    
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    for (const musician of selected) {
      setMusiciansList(prev => prev.map(m => m.id === musician.id ? { ...m, status: 'sending' } : m));
      setSendProgress(`Enviando para ${musician.name} (${sentCount + 1} de ${selected.length})...`);
      
      const phone = formatPhoneToInternational(musician.phone);
      if (!phone) {
        setMusiciansList(prev => prev.map(m => m.id === musician.id ? { ...m, status: 'error', errorMsg: 'Telefone inválido ou em branco.' } : m));
        await sleep(500);
        continue;
      }
      
      const sortedSchedules = [...musician.schedules].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (a.time || "00:00").localeCompare(b.time || "00:00");
      });
      
      let escalasText = "";
      sortedSchedules.forEach((s) => {
        escalasText += `📅 *${formatDateShort(s.date)}* (${getDayOfWeekName(s.date)}) - *${s.title}* às ${s.time}h\n`;
        escalasText += `   🎸 Função: ${s.role}\n`;
        if (s.repertoire && s.repertoire.length > 0) {
          escalasText += `   🎵 Repertório:\n`;
          s.repertoire.forEach((song) => {
            const key = song.customKey || song.key || "?";
            escalasText += `      • ${song.title}${song.artist ? ` - ${song.artist}` : ''} (${key})\n`;
          });
        }
        escalasText += "\n";
      });
      
      const message = wpMessageTemplate
        .replace(/{musico}/g, musician.name.split(" ")[0])
        .replace(/{escalas}/g, escalasText.trim());
        
      const url = wpApiUrl.replace(/\/+$/, '');
      
      try {
        const resp = await fetch(`${url}/message/sendText/${wpInstanceName}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': wpApiKey
          },
          body: JSON.stringify({
            number: phone,
            text: message
          })
        });
        
        if (resp.ok) {
          setMusiciansList(prev => prev.map(m => m.id === musician.id ? { ...m, status: 'sent', errorMsg: '' } : m));
          sentCount++;
        } else {
          const errData = await resp.json().catch(() => ({}));
          console.error("Erro Evolution API:", errData);
          const errDetail = JSON.stringify(errData, null, 2);
          setMusiciansList(prev => prev.map(m => m.id === musician.id ? { ...m, status: 'error', errorMsg: `HTTP ${resp.status}` } : m));
          setErrorModal({
            title: `Erro ao enviar para ${musician.name} (HTTP ${resp.status})`,
            message: errDetail
          });
        }
      } catch (err) {
        setMusiciansList(prev => prev.map(m => m.id === musician.id ? { ...m, status: 'error', errorMsg: err.message } : m));
      }
      
      if (musician.id !== selected[selected.length - 1].id) {
        await sleep(2000);
      }
    }
    
    setSendProgress(`Concluído! ${sentCount} de ${selected.length} lembrete(s) enviado(s) com sucesso.`);
    setSendingReminders(false);
  };

  if (loading) return <div className="p-8">Carregando...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Configuração da Igreja</h1>

      <div className="space-y-8">
          {/* CARD 1: IDENTIDADE E AVISOS */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-brand-blue">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-brand-blue p-3 rounded-full text-white"><FaChurch size={24} /></div>
              <div><h3 className="text-lg font-bold text-gray-700">Identidade & Avisos</h3><p className="text-sm text-gray-500">Defina dados gerais.</p></div>
            </div>
            <form onSubmit={handleSaveConfig} className="space-y-4">
              <div><label className="block text-sm font-bold text-gray-700 mb-1">Nome da Igreja</label><input value={name} onChange={(e) => setName(e.target.value)} className="w-full border p-2 rounded focus:border-brand-blue outline-none" /></div>
              <div><label className="block text-sm font-bold text-gray-700 mb-1">URL da Logo</label><input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} className="w-full border p-2 rounded focus:border-brand-blue outline-none" /></div>
              <div><label className="block text-sm font-bold text-gray-700 mb-1">Quadro de Avisos</label><textarea value={noticeBoard} onChange={(e) => setNoticeBoard(e.target.value)} className="w-full border p-2 rounded h-24 resize-none"></textarea></div>
              <div className="flex justify-end mt-4"><button type="submit" className="bg-brand-gold text-brand-blue font-bold px-6 py-3 rounded hover:opacity-90 flex items-center gap-2"><FaSave /> Salvar</button></div>
            </form>
          </div>

          {/* CARD 2: CULTOS E HORÁRIOS */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-brand-gold">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                    <div className="bg-brand-gold p-3 rounded-full text-brand-blue"><FaClock size={24} /></div>
                    <div><h3 className="text-lg font-bold text-gray-700">Cultos Oficiais</h3><p className="text-sm text-gray-500">Usados no Gerador Automático.</p></div>
                </div>
                <button onClick={() => { if(isCultFormOpen) handleCancelCult(); else setIsCultFormOpen(true); }} className="bg-brand-blue text-white px-3 py-1 rounded text-sm flex items-center gap-2"><FaPlus /> {isCultFormOpen ? 'Cancelar' : 'Novo'}</button>
            </div>

            {isCultFormOpen && (
                <form onSubmit={handleSaveCult} className="bg-gray-50 p-4 rounded mb-4 border border-gray-200 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><label className="block text-xs font-bold text-gray-500">Nome</label><input value={cultName} onChange={e => setCultName(e.target.value)} className="w-full border p-2 rounded" required /></div>
                        <div><label className="block text-xs font-bold text-gray-500">Dia</label><select value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)} className="w-full border p-2 rounded bg-white">{Object.entries(daysMap).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
                        <div><label className="block text-xs font-bold text-gray-500">Hora</label><input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full border p-2 rounded" required /></div>
                    </div>
                    <div className="mt-3 text-right"><button type="submit" className="bg-brand-gold text-brand-blue font-bold px-4 py-1 rounded text-sm">{editCultId ? 'Atualizar' : 'Salvar'}</button></div>
                </form>
            )}

            <div className="space-y-2">
                {cults.map(cult => (
                    <div key={cult.id} className="flex justify-between items-center p-3 bg-gray-50 rounded border hover:bg-gray-100">
                        <div><span className="font-bold text-gray-800">{cult.name}</span><span className="text-sm text-gray-500 ml-2">{daysMap[cult.dayOfWeek]} às {cult.time}h</span></div>
                        <div className="flex gap-2">
                            <button onClick={() => handleEditCult(cult)} className="text-blue-500"><FaEdit /></button>
                            <button onClick={() => handleDeleteCult(cult.id)} className="text-red-500"><FaTrash /></button>
                        </div>
                    </div>
                ))}
                {cults.length === 0 && <p className="text-center text-gray-400 italic">Nenhum culto cadastrado.</p>}
            </div>
          </div>

          {/* CARD 3: WHATSAPP / EVOLUTION API */}
          <div className="bg-white p-6 rounded-lg shadow-md border-t-4 border-green-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-green-500 p-3 rounded-full text-white"><FaWhatsapp size={24} /></div>
              <div>
                <h3 className="text-lg font-bold text-gray-700">WhatsApp - Envio de Escalas</h3>
                <p className="text-sm text-gray-500">Configuração de envio automático via Evolution API.</p>
              </div>
            </div>

            {/* Feedback */}
            {wpFeedback && (
              <div className={`mb-4 p-3 rounded-lg text-sm font-bold flex items-center gap-2 animate-fade-in ${wpFeedback.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                {wpFeedback.type === 'error' ? <FaTimesCircle /> : <FaCheckCircle />}
                {wpFeedback.msg}
              </div>
            )}

            {/* Ativar/Desativar */}
            <div className="flex items-center justify-between mb-6 bg-gray-50 p-4 rounded-lg border">
              <div>
                <span className="font-bold text-gray-700">Envio automático de lembretes</span>
                <p className="text-xs text-gray-500">Quando ativo, envia a escala semanal para os músicos no dia e hora configurados.</p>
              </div>
              <button
                onClick={() => setWpEnabled(!wpEnabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${wpEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${wpEnabled ? 'translate-x-7' : 'translate-x-0.5'}`}></div>
              </button>
            </div>

            {/* Credenciais da API */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider flex items-center gap-2"><FaPlug /> Credenciais da API</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">URL da API</label>
                  <input
                    value={wpApiUrl}
                    onChange={(e) => setWpApiUrl(e.target.value)}
                    className="w-full border p-2 rounded text-sm focus:border-green-500 outline-none"
                    placeholder="https://evolution-api.wtechsystem.com.br"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Nome da Instância</label>
                  <input
                    value={wpInstanceName}
                    onChange={(e) => setWpInstanceName(e.target.value)}
                    className="w-full border p-2 rounded text-sm focus:border-green-500 outline-none"
                    placeholder="louvor-ibgc"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">API Key (Global)</label>
                  <input
                    type="password"
                    value={wpApiKey}
                    onChange={(e) => setWpApiKey(e.target.value)}
                    className="w-full border p-2 rounded text-sm focus:border-green-500 outline-none"
                    placeholder="Chave de autenticação"
                  />
                </div>
              </div>
            </div>

            {/* Agendamento */}
            <div className="space-y-4 mb-6">
              <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider flex items-center gap-2"><FaClock /> Agendamento do Disparo</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Dia da Semana</label>
                  <select
                    value={wpSendDay}
                    onChange={(e) => setWpSendDay(e.target.value)}
                    className="w-full border p-2 rounded text-sm bg-white focus:border-green-500 outline-none"
                  >
                    {Object.entries(daysMap).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 mb-1">Horário do Envio</label>
                  <select
                    value={wpSendHour}
                    onChange={(e) => setWpSendHour(e.target.value)}
                    className="w-full border p-2 rounded text-sm bg-white focus:border-green-500 outline-none"
                  >
                    {hoursOptions.map(h => <option key={h.val} value={h.val}>{h.label}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                O sistema verificará automaticamente a cada hora e enviará os lembretes no dia e horário selecionados. 
                Será enviada a escala da semana seguinte (domingo a sábado).
              </p>
            </div>

            {/* Template da Mensagem */}
            <div className="space-y-2 mb-6">
              <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider">Template da Mensagem</h4>
              <textarea
                value={wpMessageTemplate}
                onChange={(e) => setWpMessageTemplate(e.target.value)}
                className="w-full border p-2 rounded h-32 text-sm resize-none focus:border-green-500 outline-none font-mono"
                placeholder="Use {musico} para o nome e {escalas} para a lista de escalas."
              ></textarea>
              <p className="text-xs text-gray-400">
                Tags disponíveis: <code className="bg-gray-100 px-1 rounded">{'{musico}'}</code> = nome do músico,{' '}
                <code className="bg-gray-100 px-1 rounded">{'{escalas}'}</code> = lista detalhada das escalas com repertório.
              </p>
            </div>

            {/* Botão Salvar Configurações */}
            <div className="flex justify-end mb-6">
              <button
                onClick={handleSaveWhatsApp}
                disabled={wpSaving}
                className="bg-green-500 text-white font-bold px-6 py-3 rounded hover:bg-green-600 transition flex items-center gap-2 disabled:opacity-50"
              >
                {wpSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                Salvar Configurações do WhatsApp
              </button>
            </div>

            <hr className="my-6" />

            {/* CONEXÃO / QR CODE */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider flex items-center gap-2"><FaQrcode /> Conexão do WhatsApp</h4>

              {/* Status da Conexão */}
              <div className={`p-4 rounded-lg border-2 flex items-center justify-between ${wpStatus === 'connected' ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${wpStatus === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                  <div>
                    <span className={`font-bold ${wpStatus === 'connected' ? 'text-green-700' : 'text-gray-600'}`}>
                      {wpStatus === 'connected' ? 'Conectado' : wpStatus === 'waiting_scan' ? 'Aguardando leitura do QR Code...' : wpStatus === 'qr_loading' ? 'Gerando QR Code...' : 'Desconectado'}
                    </span>
                    {wpStatus === 'connected' && <p className="text-xs text-green-600">Instância: {wpInstanceName}</p>}
                  </div>
                </div>

                <div className="flex gap-2">
                  {wpStatus === 'connected' ? (
                    <>
                      <button
                        onClick={handleTestSend}
                        disabled={wpTestSending}
                        className="bg-blue-500 text-white px-3 py-2 rounded text-sm font-bold flex items-center gap-2 hover:bg-blue-600 disabled:opacity-50"
                      >
                        {wpTestSending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />} Enviar Teste
                      </button>
                      <button
                        onClick={handleDisconnectWhatsApp}
                        className="bg-red-100 text-red-600 px-3 py-2 rounded text-sm font-bold flex items-center gap-2 hover:bg-red-200"
                      >
                        <FaTimesCircle /> Desconectar
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleConnectWhatsApp}
                      disabled={wpStatus === 'qr_loading' || wpStatus === 'waiting_scan'}
                      className="bg-green-500 text-white px-4 py-2 rounded text-sm font-bold flex items-center gap-2 hover:bg-green-600 disabled:opacity-50"
                    >
                      {wpStatus === 'qr_loading' ? <FaSpinner className="animate-spin" /> : <FaQrcode />}
                      {wpStatus === 'qr_loading' ? 'Gerando...' : 'Conectar WhatsApp'}
                    </button>
                  )}
                </div>
              </div>

              {/* QR Code Display */}
              {wpQrCode && (
                <div className="flex flex-col items-center py-6 animate-fade-in">
                  <p className="text-sm text-gray-600 mb-4 font-bold">📱 Abra o WhatsApp no celular → Dispositivos Conectados → Escanear QR Code</p>
                  <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-green-200">
                    <img src={wpQrCode} alt="QR Code WhatsApp" className="w-64 h-64" />
                  </div>
                  <p className="text-xs text-gray-400 mt-3">O QR Code expira em 60 segundos.</p>
                </div>
              )}
            </div>

            {/* DISPARO MANUAL */}
            <>
                <hr className="my-6" />
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider flex items-center gap-2">
                      <FaPaperPlane /> Disparo Manual / Teste de Lembretes
                    </h4>
                    
                    {/* Seletor de Semana */}
                    <div className="flex items-center gap-2 self-start sm:self-auto">
                      <span className="text-xs font-bold text-gray-500">Semana:</span>
                      <select
                        value={referenceWeek}
                        onChange={(e) => setReferenceWeek(e.target.value)}
                        className="border p-1.5 rounded text-xs bg-white focus:border-green-500 outline-none font-medium"
                        disabled={sendingReminders}
                      >
                        <option value="current">Semana Atual (Esta semana)</option>
                        <option value="next">Próxima Semana (Seguinte)</option>
                      </select>
                    </div>
                  </div>

                  {loadingMusicians ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
                      <FaSpinner className="animate-spin text-green-500" />
                      <span>Carregando escala e músicos...</span>
                    </div>
                  ) : musiciansList.length === 0 ? (
                    <p className="text-sm text-gray-400 italic text-center py-6">
                      Nenhuma escala encontrada para o período selecionado.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="overflow-x-auto border rounded-lg max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                              <th className="p-3 text-left w-12 bg-gray-50">
                                <input
                                  type="checkbox"
                                  checked={musiciansList.every(m => m.checked)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setMusiciansList(prev => prev.map(m => ({ ...m, checked })));
                                  }}
                                  disabled={sendingReminders}
                                  className="rounded text-green-600 focus:ring-green-500 h-4 w-4 cursor-pointer"
                                />
                              </th>
                              <th className="p-3 text-left font-bold text-gray-600 bg-gray-50">Músico</th>
                              <th className="p-3 text-left font-bold text-gray-600 bg-gray-50">Escalas da Semana</th>
                              <th className="p-3 text-left font-bold text-gray-600 bg-gray-50">Telefone</th>
                              <th className="p-3 text-left font-bold text-gray-600 bg-gray-50">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {musiciansList.map((musician) => (
                              <tr key={musician.id} className="hover:bg-gray-50 transition-colors">
                                <td className="p-3">
                                  <input
                                    type="checkbox"
                                    checked={musician.checked}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setMusiciansList(prev => prev.map(m => m.id === musician.id ? { ...m, checked } : m));
                                    }}
                                    disabled={sendingReminders}
                                    className="rounded text-green-600 focus:ring-green-500 h-4 w-4 cursor-pointer"
                                  />
                                </td>
                                <td className="p-3 font-semibold text-gray-800">{musician.name}</td>
                                <td className="p-3 text-xs text-gray-600 space-y-1">
                                  {musician.schedules.map((s, idx) => (
                                    <div key={idx} className="flex flex-col md:flex-row md:items-center gap-1">
                                      <span className="font-bold bg-green-50 text-green-700 px-1.5 py-0.5 rounded border border-green-100 text-[10px] whitespace-nowrap self-start">
                                        {formatDateShort(s.date)} ({getDayOfWeekName(s.date)})
                                      </span>
                                      <span className="font-medium text-gray-700">{s.title}</span>
                                      <span className="text-gray-400">({s.role})</span>
                                    </div>
                                  ))}
                                </td>
                                <td className="p-3 text-xs">
                                  {musician.phone ? (
                                    <span className="text-gray-700 font-mono">{musician.phone}</span>
                                  ) : (
                                    <span className="bg-red-50 text-red-600 border border-red-100 px-2 py-1 rounded text-[10px] font-bold">Sem Telefone</span>
                                  )}
                                </td>
                                <td className="p-3">
                                  {musician.status === 'pending' && (
                                    <span className="text-gray-400 text-xs flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300"></span> Aguardando</span>
                                  )}
                                  {musician.status === 'sending' && (
                                    <span className="text-blue-500 text-xs font-bold flex items-center gap-1 animate-pulse"><FaSpinner className="animate-spin" /> Enviando...</span>
                                  )}
                                  {musician.status === 'sent' && (
                                    <span className="text-green-600 text-xs font-bold flex items-center gap-1"><FaCheckCircle /> Enviado</span>
                                  )}
                                  {musician.status === 'error' && (
                                    <span className="text-red-500 text-xs font-bold flex flex-col" title={musician.errorMsg}>
                                      <span className="flex items-center gap-1"><FaTimesCircle /> Erro</span>
                                      <span className="text-[10px] font-normal text-red-400 max-w-[150px] truncate">{musician.errorMsg}</span>
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Progresso e Disparo */}
                      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50 p-4 rounded-lg border">
                        <div className="text-xs text-gray-500">
                          {sendingReminders ? (
                            <span className="font-bold text-blue-600">{sendProgress}</span>
                          ) : !wpConnected ? (
                            <span className="text-red-500 font-bold">⚠️ Conecte o WhatsApp no painel acima para disparar os lembretes.</span>
                          ) : (
                            <span>
                              Músicos selecionados: <strong className="text-gray-700">{musiciansList.filter(m => m.checked).length}</strong> de <strong className="text-gray-700">{musiciansList.length}</strong>
                            </span>
                          )}
                        </div>
                        <button
                          onClick={handleSendReminders}
                          disabled={sendingReminders || !wpConnected || musiciansList.filter(m => m.checked).length === 0}
                          className="bg-green-500 text-white font-bold px-6 py-2.5 rounded text-sm hover:bg-green-600 transition flex items-center gap-2 disabled:opacity-50 w-full md:w-auto justify-center"
                        >
                          {sendingReminders ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                          Disparar Lembretes para Selecionados
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
          </div>
      </div>

      {/* MODAL DE ERRO ELEGANTE */}
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="bg-red-500 px-6 py-4 flex items-center gap-3">
              <FaTimesCircle className="text-white text-xl flex-shrink-0" />
              <h3 className="text-white font-bold text-lg truncate">{errorModal.title}</h3>
            </div>
            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-500 mb-3">Detalhes retornados pela Evolution API:</p>
              <pre className="bg-gray-100 border border-gray-200 rounded-lg p-4 text-xs text-gray-800 overflow-auto max-h-60 whitespace-pre-wrap break-words font-mono">
                {errorModal.message}
              </pre>
            </div>
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end">
              <button
                onClick={() => setErrorModal(null)}
                className="bg-gray-700 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-gray-800 transition"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}